/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : VehicleClass.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */
/**
 * @author Abdul Rahim M - abdul@au79consulting.com
 * @date 1-07-2024
 * @description Component for handling the Vehicle Class.
 */
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogContentText,
  IconButton,
  Stack,
  Typography,
  LinearProgress,
} from "@mui/material";
import DefaultProfilePic from "./../../assets/logoIcons/defaultProfilePic.png";

import deleteIcon from "../../assets/delete_icon.png";
import BorderColorIcon from "@mui/icons-material/BorderColor";
import React, { useCallback, useEffect, useRef, useState } from "react";

import { useDispatch, useSelector } from "react-redux";

import customConsole from "../../config/customConsole";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CloseIcon from "@mui/icons-material/Close";

import { HandleApiActions } from "../../redux/actions/actionHandler";

import { toast } from "react-toastify";
import profileActions from "../../redux/actions/profile";

import adminSettingsAPI from "../../redux/actions/adminSetttingAPI";
import { convertUtcToTimeZone } from "../../utils/convertUtcToTimeZone";
import CommonBreadcrumbs from "../../components/customComponentsV2/common/Breadcrumbs/CommonBreadcrumbs";
import CustomSearchbarWithApiV2 from "../../components/customComponentsV2/common/searchBar/CustomSearchbarWithApiV2";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import AddButtonV2 from "../../components/customComponentsV2/common/button/AddButtonV2";
import CustomDataGridV2 from "../../components/customComponentsV2/common/datagrid/CustomDataGridV2";
import CustomDialogBoxV2 from "../../components/customComponentsV2/common/customDialogBox/CustomDialogBoxV2";
import CustomFormTextFieldV2 from "../../components/customComponentsV2/common/textField/CustomFormTextFieldV2";
import * as XLSX from "xlsx";
import CustomButtonV2 from "../../components/customComponentsV2/common/button/CustomButtonV2";
import PropTypes from "prop-types";

const getVehicleClassList = (resp) => {
  if (Array.isArray(resp?.data)) return resp.data;
  if (Array.isArray(resp?.response?.data)) return resp.response.data;
  return [];
};

const handleProfileUploadCommon = ({
  event,
  dispatch,
  setSelectedProfilePic,
  setIsUploading,
  setUploadProgress,
}) => {
  const file = event.target.files[0];

  if (!file) return;

  const fileExtension = file.name.split(".").pop().toLowerCase();
  const allowedExtensions = ["jpg", "jpeg", "png"];
  const maxSize = 800 * 1024;

  if (!allowedExtensions.includes(fileExtension)) {
    toast.dark("Please select a valid file (JPG or PNG)", {
      type: "error",
      position: "top-right",
      autoClose: 3000,
    });
    return;
  }

  if (file.size > maxSize) {
    toast.dark("File size should not exceed 800KB", {
      type: "error",
      position: "top-right",
      autoClose: 3000,
    });
    return;
  }

  setSelectedProfilePic(file);
  setIsUploading(true);
  setUploadProgress(0);

  const interval = setInterval(() => {
    setUploadProgress((prev) => {
      if (prev >= 100) {
        clearInterval(interval);
        setIsUploading(false);
        return 100;
      }
      return prev + 10;
    });
  }, 100);

  dispatch(
    HandleApiActions({
      ...profileActions.upload_profile_pic,
      show_toast: false,
      file: file,
      is_file: true,
    }),
  );
};

function VehicleClass({ open, handleClick }) {
  // Add resize detection state
  const [tableKey, setTableKey] = useState(0);
  const [totalVehicleClassCount, setTotalVehicleClassCount] = useState(0);

  // Force table re-render when component becomes active
  useEffect(() => {
    if (open === "vehicle-classes") {
      setTableKey((prev) => prev + 1);
    }
  }, [open]);

  const COLUMNS = [
    {
      field: "sno",
      headerName: "S.No",
      flex: 0.3,
      minWidth: 60,
    },
    {
      field: "name",
      headerName: "Name",
      flex: 1,
      minWidth: 120,
    },
    {
      field: "class",
      headerName: "Class",
      flex: 0.6,
      minWidth: 80,
    },
    {
      field: "sub_class",
      headerName: "Sub Class",
      flex: 0.6,
      minWidth: 80,
    },
    {
      field: "description",
      headerName: "Description",
      flex: 0.8,
      minWidth: 120,
    },
    {
      field: "created_at",
      headerName: "Created At",
      flex: 0.6,
      minWidth: 100,
    },
    {
      field: "updated_at",
      headerName: "Updated At",
      flex: 0.6,
      minWidth: 100,
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 0.4,
      minWidth: 100,
      sortable: false,
      renderCell: (params) => (
        <Box sx={{ display: "flex", gap: 0.5 }}>
          <IconButton
            size="small"
            sx={{ color: "#000000", p: 0.5 }}
            onClick={(e) => {
              e.stopPropagation();
              handleTableRowClick(params, "3");
            }}
          >
            <VisibilityIcon sx={{ fontSize: 20 }} />
          </IconButton>
          <IconButton
            size="small"
            sx={{ color: "#000000", p: 0.5 }}
            onClick={(e) => {
              e.stopPropagation();
              handleTableRowClick(params, "2");
            }}
          >
            <BorderColorIcon fontSize="inherit" sx={{ fontSize: 19 }} />
          </IconButton>
          <IconButton
            size="small"
            sx={{ p: 0 }}
            onClick={(e) => {
              e.stopPropagation();
              handleTableRowClick(params, "1");
            }}
          >
            <img
              src={deleteIcon}
              alt="delete"
              style={{ width: 24, height: 24 }}
            />
          </IconButton>
        </Box>
      ),
    },
  ];
  //1.Redux
  const dispatch = useDispatch();
  const vehicleClassDetails = useSelector((state) => state.vehicleClassReducer);
  const profilePathDetails = useSelector((state) => state?.profileReducer);
  const timeZoneDetails = useSelector((state) => state.profileReducer);

  //2.React useState
  const [page, setPage] = React.useState(1); // Changed to 1-based pagination like PowerTrainClass
  const [tableRowsData, setTableRowsData] = useState([]);
  const [openModal, setOpenModal] = React.useState(false);
  const [rowsPerPage] = React.useState(25);
  const [getVehicleClass, setGetVehicleClass] = React.useState([]);
  const [profilePicPath, setProfilePicPath] = React.useState("");
  const [profilePng, setProfilePng] = React.useState("");

  const [openAddVehicleClassDialog, setOpenAddVehicleClassDialog] =
    React.useState(false);
  const [openEditVehicleClassDialog, setOpenEditVehicleClassDialog] =
    React.useState(false);
  const [selectedRow, setSelectedRow] = React.useState(null);
  const [deleteVehicleClassDialog, setDeleteVehicleClassDialog] =
    React.useState(false);
  const [searchQuery] = useState("");
  //3.React useEffect

  const supplierRef = useRef(null);
  useEffect(() => {
    if (open === "5") {
      const timeout = setTimeout(() => {
        if (
          supplierRef.current &&
          supplierRef.current.scrollHeight > supplierRef.current.clientHeight
        ) {
          supplierRef.current.scrollTop = supplierRef.current.scrollHeight;
        }
      }, 300);
      return () => clearTimeout(timeout);
    }
  }, [open]);
  useEffect(() => {
    let filteredUsersList = getVehicleClass?.map((data, index) => {
      return {
        ...data,
        sno: (page - 1) * rowsPerPage + index + 1, // Calculate serial number based on pagination
        id: data?.id,

        name: data?.name,
        class: data?.class,
        subclass: data?.subclass,
        description: data?.other_fields?.description,
        created_at: convertUtcToTimeZone(
          data?.created_at,
          timeZoneDetails?.profile?.profile?.region,
        ),
        updated_at: convertUtcToTimeZone(
          data?.updated_at,
          timeZoneDetails?.profile?.profile?.region,
        ),
      };
    });

    setTableRowsData(filteredUsersList || []); // Ensure it's always an array
  }, [
    getVehicleClass,
    page,
    rowsPerPage,
    timeZoneDetails?.profile?.profile?.region,
  ]);

  const filteredRows = searchQuery
    ? tableRowsData.filter((row) =>
        COLUMNS.some((column) =>
          row[column.field]
            ?.toString()
            .toLowerCase()
            .includes(searchQuery.toLowerCase()),
        ),
      )
    : tableRowsData;

  useEffect(() => {
    dispatch(HandleApiActions({ ...profileActions.get_coutry_regions }));
  }, [dispatch]);
  useEffect(() => {
    const region = timeZoneDetails?.profile?.profile?.region;
    if (region?.length > 0) {
      customConsole("timeZoneDetails?.data?.profile?.region");
      customConsole(region);
    }
  }, [timeZoneDetails]);

  useEffect(() => {
    if (
      profilePathDetails?.profile_pic_name &&
      profilePathDetails?.profile_pic_name.length > 0
    ) {
      customConsole("profilePathDetails?.profile_pic_name");
      customConsole(profilePathDetails?.profile_pic_name);
      setProfilePng(profilePathDetails?.profile_pic_name);
    }
  }, [profilePathDetails]);

  useEffect(() => {
    if (profilePng?.length) {
      dispatch(
        HandleApiActions({
          ...profileActions.get_profile_pic,
          show_toast: true,
          file_name: profilePng,
          is_file: true,
        }),
      );
    }
  }, [dispatch, profilePng]);

  // API call function with pagination support similar to PowerTrainClass
  const getVehicleClassDetails = useCallback(
    async (pageOverride) => {
      const pageToUse = pageOverride ?? page;
      const resp = await dispatch(
        HandleApiActions({
          ...adminSettingsAPI.get_vehicle_class,
          show_toast: false,
          params: {
            page_number: pageToUse,
            page_size: rowsPerPage,
          },
        }),
      );

      if (resp?.error) {
        setGetVehicleClass([]);
        setTableRowsData([]);
        setTotalVehicleClassCount(0);
        return { list: [], totalCount: 0 };
      }

      const list = getVehicleClassList(resp);

      const totalCount =
        resp?.total_count ??
        resp?.totalItems ??
        resp?.total ??
        (Array.isArray(list) ? list.length : 0);

      setGetVehicleClass(list);
      setTotalVehicleClassCount(totalCount);

      return { list, totalCount };
    },
    [dispatch, page, rowsPerPage],
  );

  useEffect(() => {
    getVehicleClassDetails();
  }, [page, rowsPerPage, getVehicleClassDetails]);

  useEffect(() => {
    customConsole("profilePathDetails?.profile_pic_path");
    customConsole(profilePathDetails?.profile_pic_path);
    setProfilePicPath(profilePathDetails?.profile_pic_path);
  }, [profilePathDetails]);

  useEffect(() => {
    customConsole("VehicleClass Train Details");
    customConsole(vehicleClassDetails);
  }, [vehicleClassDetails]);

  //4.Functions
  const toggleDialog = (newOpen) => () => {
    setOpenAddVehicleClassDialog(newOpen);
  };

  const handleAddVehicleClassClick = () => {
    toggleDialog(true)();
  };

  function handleTableRowClick(row, isButtonClicked) {
    setSelectedRow(row);

    customConsole("Button Clicked: " + isButtonClicked);

    switch (isButtonClicked) {
      case "1":
        setDeleteVehicleClassDialog(true);
        setOpenEditVehicleClassDialog(false);
        setOpenModal(false);
        break;
      case "2":
        setOpenEditVehicleClassDialog(true);
        setDeleteVehicleClassDialog(false);
        setOpenModal(false);
        break;
      case "3":
        setOpenModal(true);
        setOpenEditVehicleClassDialog(false);
        setDeleteVehicleClassDialog(false);
        customConsole("Row.image");
        customConsole(row?.image);
        dispatch(
          HandleApiActions({
            ...profileActions.get_profile_pic,
            show_toast: true,
            file_name: row?.row?.image,
            is_file: true,
          }),
        );
        break;
      default:
        break;
    }
  }

  // Function to handle export to Excel
  const handleExportToExcel = () => {
    if (!filteredRows || filteredRows.length === 0) {
      console.warn("No data to export");
      return;
    }

    // Prepare data for export - map to clean format without internal fields
    const exportData = filteredRows.map((row) => ({
      "S.No": row.sno,
      Name: row.name,
      Class: row.class,
      "Sub Class": row.sub_class,
      Description: row.description,
      Created: row.created_at,
      Updated: row.updated_at,
    }));

    // Create a new workbook
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(exportData);

    // Add worksheet to workbook
    XLSX.utils.book_append_sheet(wb, ws, "Vehicle Classes");

    // Generate and download Excel file
    XLSX.writeFile(wb, "Vehicle_Classes.xlsx");
  };

  const breadcrumbItems = [
    { label: "Settings" },
    { label: "Feature Portfolio" },
    { label: "Vehicle Class" },
  ];

  return (
    <Box
      sx={{
        margin: 0,
        padding: 0,
        width: "100%",
        height: "100%",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        backgroundColor: "#F5F5F5",
      }}
    >
      {/* Breadcrumbs and description */}
      <Box
        sx={{
          padding: { xs: "8px 8px 0px 8px", md: "12px 12px 0px 12px" },
          flexShrink: 0,
        }}
      >
        <CommonBreadcrumbs items={breadcrumbItems} />
      </Box>

      {/* Header with Search and Add Button */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: { xs: "0 16px 12px 16px", md: "0 24px 16px 24px" },
          flexShrink: 0,
          mt: 1,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box>
            <CustomSearchbarWithApiV2
              params={{
                page_number: page,
                page_size: rowsPerPage,
              }}
              apiAction={adminSettingsAPI.get_vehicle_class}
              searchKey={"name"}
              getDataOnSearch={(data, resp) => {
                console.log("Search Data:", resp);
                if (resp?.error) {
                  setGetVehicleClass([]);
                  setTableRowsData([]);
                  setTotalVehicleClassCount(0);
                  return;
                }

                const searchedList = Array.isArray(data) ? data : [];
                setGetVehicleClass(searchedList);
                setTotalVehicleClassCount(
                  resp?.total_count ??
                    resp?.totalItems ??
                    resp?.total ??
                    searchedList.length,
                );
              }}
              placeholder={"Search"}
            />
          </Box>
          <Box>
            <AddButtonV2 handleOnClick={handleAddVehicleClassClick} />
          </Box>
        </Box>
        <Box>
          <CustomButtonV2
            startIcon={<FileDownloadOutlinedIcon />}
            type="submit"
            size="small"
            btnName="Export"
            handleOnClick={handleExportToExcel}
          />
        </Box>
      </Box>

      {/* DataGrid Container */}
      <Box
        sx={{
          height: "76vh",
          width: "100%",
          display: "flex",
          flexDirection: "column",
          padding: { xs: "0 8px", md: "0 16px" },
          mt: -1,
        }}
        key={`vehicle-class-table-${tableKey}`}
      >
        <CustomDataGridV2
          rows={filteredRows}
          columns={COLUMNS}
          isMultiLine={true}
          onRowClick={(params) => handleTableRowClick(params, "2")}
          showPagination={true}
          page={page}
          rowsPerPage={rowsPerPage}
          handlePageChange={(newPage) => setPage(newPage)}
          totalItems={totalVehicleClassCount}
        />
      </Box>

      <AddVehicleClass
        open={openAddVehicleClassDialog}
        toggleDialog={toggleDialog}
        setPage={setPage}
        refreshVehicleClass={getVehicleClassDetails}
      />
      <EditVehicleClass
        open={openEditVehicleClassDialog}
        setOpenEditVehicleClassDialog={setOpenEditVehicleClassDialog}
        selectedRow={selectedRow}
        page={page}
        refreshVehicleClass={getVehicleClassDetails}
      />

      <DeleteVehicleClass
        deleteVehicleClassDialog={deleteVehicleClassDialog}
        selectedRow={selectedRow}
        setDeleteVehicleClassDialog={setDeleteVehicleClassDialog}
        setPage={setPage}
        page={page}
        rowsPerPage={rowsPerPage}
        refreshVehicleClass={getVehicleClassDetails}
      />

      {openModal && (
        <Dialog open={openModal} onClose={() => setOpenModal(false)}>
          <DialogContent>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 2,
              }}
            >
              <Typography variant="h6">View Image</Typography>
              <IconButton onClick={() => setOpenModal(false)} color="inherit">
                <CloseIcon />
              </IconButton>
            </Box>
            <img
              alt=""
              src={profilePicPath || DefaultProfilePic}
              style={{
                height: "100%",
                width: "100%",
                objectFit: "contain", // Adjust object-fit as needed
              }}
            />
          </DialogContent>
        </Dialog>
      )}
    </Box>
  );
}

export default VehicleClass;

const selectedRowShape = PropTypes.shape({
  id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  row: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    name: PropTypes.string,
    class: PropTypes.string,
    sub_class: PropTypes.string,
    image: PropTypes.string,
    other_fields: PropTypes.shape({
      description: PropTypes.string,
    }),
  }),
});

VehicleClass.propTypes = {
  open: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  handleClick: PropTypes.func,
};

// Styling for the Users component
const AddVehicleClass = ({
  open,
  toggleDialog,
  setPage,
  refreshVehicleClass,
}) => {
  const dispatch = useDispatch();
  const profilePathDetails = useSelector((state) => state?.profileReducer);

  const [name, setName] = useState("");
  const [className, setClassName] = useState("");
  const [subClass, setSubClass] = useState("");
  const [description, setDescription] = useState("");
  const [selectedProfilePic, setSelectedProfilePic] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);

  const [profilePng, setProfilePng] = React.useState("");

  React.useEffect(() => {
    customConsole("fileNameDetailssssssssss");
    customConsole(profilePathDetails?.profile_pic_path);

    setProfilePng(profilePathDetails?.profile_pic_name);
  }, [profilePathDetails]);

  const handleClose = () => {
    setName("");
    setClassName("");
    setSubClass("");
    setDescription("");
    setSelectedProfilePic(null);
    setUploadProgress(0);
    setIsUploading(false);
    toggleDialog(false)();
  };

  const handleClickAdd = async () => {
    try {
      const reqObj = {
        current_time: new Date(),
        sub_class: subClass,
        class: className,
        name: name,
        image: profilePng,
      };

      if (profilePng === "") {
        toast.warn("Upload Pic");
      }
      if (description?.trim()) {
        reqObj.description = description;
      }
      customConsole("profilePng");
      customConsole("reqObj");
      customConsole(reqObj);

      await dispatch(
        HandleApiActions({
          ...adminSettingsAPI.add_vehicle_class,
          params: reqObj,
          show_toast: true,
        }),
      );
      toggleDialog(false)();

      setPage(1);
      await refreshVehicleClass(1);
      setName("");
      setClassName("");
      setSubClass("");
      setDescription("");
      setSelectedProfilePic(null);
      setUploadProgress(0);
      setIsUploading(false);
    } catch (error) {
      console.error("Error adding vehicle class:", error);
    }
  };

  const handelUploadProfile = (event) => {
    customConsole("Uploading vehicle class image for edit");
    handleProfileUploadCommon({
      event,
      dispatch,
      setSelectedProfilePic,
      setIsUploading,
      setUploadProgress,
    });
  };

  return (
    <CustomDialogBoxV2
      open={open}
      onClose={handleClose}
      title={"Add Vehicle Class"}
      successButtonName={"Add"}
      rejectButtonName={"Close"}
      onClickSuccess={handleClickAdd}
      onDisabled={
        !(
          name?.length > 2 &&
          name?.trim() !== "" &&
          className?.length > 2 &&
          className?.trim() !== "" &&
          subClass?.length > 2 &&
          subClass?.trim() !== "" &&
          description?.length > 2 &&
          description?.trim() !== "" &&
          selectedProfilePic
        )
      }
      actionButtonStyle={"continuous"}
      onClickReject={handleClose}
    >
      <Stack spacing={2} sx={{ width: "350px" }}>
        <CustomFormTextFieldV2
          label="Name"
          required={true}
          value={name}
          setValue={(val) => {
            if (val.length <= 35) {
              setName(val);
            }
          }}
          maxLength={35}
        />
        <CustomFormTextFieldV2
          label="Class"
          required={true}
          value={className}
          setValue={(val) => {
            if (val.length <= 35) {
              setClassName(val);
            }
          }}
        />
        <CustomFormTextFieldV2
          label="Sub Class"
          required={true}
          value={subClass}
          setValue={(val) => {
            if (val.length <= 35) {
              setSubClass(val);
            }
          }}
          maxLength={35}
        />
        <CustomFormTextFieldV2
          label="Description"
          required={true}
          value={description}
          // setValue={setDescription}
          setValue={(val) => {
            if (val.length <= 250) {
              setDescription(val);
            }
          }}
          maxLength={250}
        />
        <Box sx={{ minWidth: "270px" }}>
          <Typography sx={{ marginBottom: "8px" }}>
            Upload File<span style={{ color: "red" }}>*</span>
          </Typography>
          <Typography
            sx={{
              fontSize: "12px",
              color: "#666",
              marginBottom: "16px",
            }}
          >
            JPG or PNG. Max size of 800K
          </Typography>
          <Button
            size="small"
            variant="contained"
            component="label"
            sx={{
              backgroundColor: "#26890D",
              color: "white",
              textTransform: "none",
              "&:hover": {
                backgroundColor: "#1f6e0a",
              },
              borderRadius: "0px",
            }}
          >
            <span>Choose File</span>
            <input
              type="file"
              hidden
              accept=".jpg,.jpeg,.png"
              onChange={handelUploadProfile}
            />
          </Button>

          {/* Show selected file with remove option */}
          {selectedProfilePic && (
            <Box sx={{ marginTop: "16px" }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  padding: "6px",
                  backgroundColor: "#f9f9f9",
                  borderRadius: "0px",
                }}
              >
                <Typography
                  sx={{
                    fontSize: "12px",
                    color: "#53565A",
                  }}
                >
                  {selectedProfilePic.name}
                </Typography>
                <IconButton
                  size="small"
                  onClick={() => {
                    setSelectedProfilePic(null);
                    setUploadProgress(0);
                    setIsUploading(false);
                  }}
                  sx={{
                    padding: "4px",
                    "&:hover": {
                      backgroundColor: "#e0e0e0",
                    },
                  }}
                >
                  <CloseIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Box>

              {/* Progress Bar - Always visible when file is selected */}
              {selectedProfilePic && (
                <Box sx={{ width: "100%", marginBottom: "8px" }}>
                  <LinearProgress
                    variant="determinate"
                    value={uploadProgress}
                    sx={{
                      height: "5px",
                      borderRadius: "0px",
                      backgroundColor: "#e0e0e0",
                      "& .MuiLinearProgress-bar": {
                        backgroundColor: "#26890D",
                        borderRadius: "0px",
                      },
                    }}
                  />
                </Box>
              )}

              {!isUploading && uploadProgress === 100 && (
                <Typography
                  sx={{
                    fontSize: "12px",
                    color: "#26890D",
                  }}
                >
                  Successfully Uploaded
                </Typography>
              )}
            </Box>
          )}
        </Box>
      </Stack>
    </CustomDialogBoxV2>
  );
};

AddVehicleClass.propTypes = {
  open: PropTypes.bool,
  toggleDialog: PropTypes.func,
  setPage: PropTypes.func,
  refreshVehicleClass: PropTypes.func,
};

const EditVehicleClass = ({
  open,
  setOpenEditVehicleClassDialog,
  selectedRow,
  page,
  refreshVehicleClass,
}) => {
  const dispatch = useDispatch();
  const profilePathDetails = useSelector((state) => state?.profileReducer);

  customConsole("selectedRow");

  customConsole(selectedRow);

  const [name, setName] = useState("");
  const [className, setClassName] = useState("");
  const [subClass, setSubClass] = useState("");
  const [description, setDescription] = useState("");
  const [selectedProfilePic, setSelectedProfilePic] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);

  const [profilePng, setProfilePng] = React.useState("");

  const [initialDescription, setInitialDescription] = useState("");

  React.useEffect(() => {
    customConsole("fileNameDetailssssssssss");
    customConsole(profilePathDetails?.profile_pic_path);

    setProfilePng(profilePathDetails?.profile_pic_name);
  }, [profilePathDetails]);

  React.useEffect(() => {
    if (selectedRow) {
      setName(selectedRow?.row?.name || "");
      setClassName(selectedRow?.row?.class || "");
      setSubClass(selectedRow?.row?.sub_class || "");
      const desc = selectedRow?.row?.other_fields?.description || "";
      setDescription(desc);
      setInitialDescription(desc);
    }
  }, [selectedRow]);
  React.useEffect(() => {
    customConsole("selectedRow");
    customConsole(selectedRow);
    customConsole("name");
    customConsole(selectedRow?.row?.name);
  }, [selectedRow]);

  const handleClose = () => {
    setOpenEditVehicleClassDialog(false);
    setInitialDescription("");
    setDescription("");
    setSelectedProfilePic(null);
    setUploadProgress(0);
    setIsUploading(false);
  };

  const normalizeText = (val) => (val ?? "").toString().trim();
  const isDescriptionValid = normalizeText(description).length > 2;
  const isDescriptionChanged =
    normalizeText(description) !== normalizeText(initialDescription);
  const isImageChanged = Boolean(selectedProfilePic);
  const isUpdateEnabled =
    Boolean(selectedRow) &&
    isDescriptionValid &&
    (isDescriptionChanged || isImageChanged);

  const handleClickEdit = async () => {
    try {
      const selectedRowId = selectedRow?.row?.id;

      const reqObj = {
        current_time: new Date(),
        id: selectedRowId ? Number.parseInt(selectedRowId, 10) : null,
        ...(profilePng?.length > 0 ? { image: profilePng } : {}),
        ...(description?.trim().length > 0
          ? { description: description.trim() }
          : {}),
      };

      const resp = await dispatch(
        HandleApiActions({
          ...adminSettingsAPI.update_vehicle_class,
          params: reqObj,
          show_toast: true,
        }),
      );

      if (!resp?.error) {
        setOpenEditVehicleClassDialog(false);
      }

      await refreshVehicleClass(page);

      setProfilePng("");
      setDescription("");
      setSelectedProfilePic(null);
      setUploadProgress(0);
      setIsUploading(false);
    } catch (error) {
      console.error("Error updating vehicle class:", error);
    }
  };

  const handelUploadProfile = (event) => {
    handleProfileUploadCommon({
      event,
      dispatch,
      setSelectedProfilePic,
      setIsUploading,
      setUploadProgress,
    });
  };

  return (
    <CustomDialogBoxV2
      open={open}
      onClose={handleClose}
      title={"Update Vehicle Class"}
      successButtonName={"Update"}
      rejectButtonName={"Close"}
      onClickSuccess={handleClickEdit}
      onDisabled={!isUpdateEnabled}
      actionButtonStyle={"continuous"}
      onClickReject={handleClose}
    >
      <Stack spacing={2} sx={{ width: "350px" }}>
        <CustomFormTextFieldV2
          label="Name"
          required={true}
          value={name}
          setValue={(val) => {
            if (val.length <= 35) {
              setName(val);
            }
          }}
          disabled={true}
          maxLength={35}
        />
        <CustomFormTextFieldV2
          label={"Class"}
          value={className}
          // setValue={setClassName}
          setValue={(val) => {
            if (val.length <= 35) {
              setClassName(val);
            }
          }}
          disabled={true}
          required={true}
        />
        <CustomFormTextFieldV2
          label={"Sub Class"}
          value={subClass}
          setValue={(val) => {
            if (val.length <= 35) {
              setSubClass(val);
            }
          }}
          disabled={true}
          required={true}
        />
        <CustomFormTextFieldV2
          label={"Description"}
          value={description}
          setValue={(val) => {
            if (val.length <= 250) {
              setDescription(val);
            }
          }}
          required={true}
          maxLength={250}
        />
        <Box sx={{ minWidth: "270px" }}>
          <Typography sx={{ marginBottom: "8px" }}>
            Upload File<span style={{ color: "red" }}>*</span>
          </Typography>
          <Typography
            sx={{
              fontSize: "12px",
              color: "#666",
              marginBottom: "16px",
            }}
          >
            JPG or PNG. Max size of 800K
          </Typography>
          <Button
            size="small"
            variant="contained"
            component="label"
            sx={{
              backgroundColor: "#26890D",
              color: "white",
              textTransform: "none",
              "&:hover": {
                backgroundColor: "#1f6e0a",
              },
              borderRadius: "0px",
            }}
          >
            <span>Choose File</span>
            <input
              type="file"
              hidden
              accept=".jpg,.jpeg,.png"
              onChange={handelUploadProfile}
            />
          </Button>

          {/* Show selected file with remove option */}
          {selectedProfilePic && (
            <Box sx={{ marginTop: "16px" }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  padding: "6px",
                  backgroundColor: "#f9f9f9",
                  borderRadius: "0px",
                }}
              >
                <Typography
                  sx={{
                    fontSize: "12px",
                    color: "#53565A",
                  }}
                >
                  {selectedProfilePic.name}
                </Typography>
                <IconButton
                  size="small"
                  onClick={() => {
                    setSelectedProfilePic(null);
                    setUploadProgress(0);
                    setIsUploading(false);
                  }}
                  sx={{
                    padding: "4px",
                    "&:hover": {
                      backgroundColor: "#e0e0e0",
                    },
                  }}
                >
                  <CloseIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Box>

              {/* Progress Bar - Always visible when file is selected */}
              {selectedProfilePic && (
                <Box sx={{ width: "100%", marginBottom: "8px" }}>
                  <LinearProgress
                    variant="determinate"
                    value={uploadProgress}
                    sx={{
                      height: "5px",
                      borderRadius: "0px",
                      backgroundColor: "#e0e0e0",
                      "& .MuiLinearProgress-bar": {
                        backgroundColor: "#26890D",
                        borderRadius: "0px",
                      },
                    }}
                  />
                </Box>
              )}

              {!isUploading && uploadProgress === 100 && (
                <Typography
                  sx={{
                    fontSize: "12px",
                    color: "#26890D",
                  }}
                >
                  Successfully Uploaded
                </Typography>
              )}
            </Box>
          )}
        </Box>
      </Stack>
    </CustomDialogBoxV2>
  );
};

EditVehicleClass.propTypes = {
  open: PropTypes.bool,
  setOpenEditVehicleClassDialog: PropTypes.func,
  selectedRow: selectedRowShape,
  page: PropTypes.number,
  refreshVehicleClass: PropTypes.func,
};

const DeleteVehicleClass = ({
  deleteVehicleClassDialog,
  selectedRow,
  setDeleteVehicleClassDialog,
  setPage,
  page,
  rowsPerPage,
  refreshVehicleClass,
}) => {
  const dispatch = useDispatch();

  const handleDelete = async () => {
    try {
      const reqObj = {
        current_time: new Date(),
        id: selectedRow?.id,
      };

      await dispatch(
        HandleApiActions({
          ...adminSettingsAPI.delete_vehicle_class,
          params: reqObj,
          show_toast: true,
        }),
      );
      setDeleteVehicleClassDialog(false);

      const refreshResult = await refreshVehicleClass(page);
      const totalCount = refreshResult?.totalCount ?? 0;
      if (page > 1 && totalCount <= (page - 1) * rowsPerPage) {
        const newPage = page - 1;
        setPage(newPage);
        await refreshVehicleClass(newPage);
      }
    } catch (error) {
      console.error("Error deleting vehicle class:", error);
    }
  };
  return (
    <CustomDialogBoxV2
      open={deleteVehicleClassDialog}
      onClose={() => setDeleteVehicleClassDialog(false)}
      title={"Delete Vehicle Class"}
      successButtonName={"Ok"}
      rejectButtonName={"Cancel"}
      onClickSuccess={handleDelete}
      onClickReject={() => setDeleteVehicleClassDialog(false)}
      actionButtonStyle={"continuous"}
    >
      <Box
        sx={{
          width: "350px",
          height: "50px",
          display: "flex",
          alignItems: "center",
        }}
      >
        <DialogContentText>Are you sure you want to delete?</DialogContentText>
      </Box>
    </CustomDialogBoxV2>
  );
};

DeleteVehicleClass.propTypes = {
  deleteVehicleClassDialog: PropTypes.bool,
  selectedRow: selectedRowShape,
  setDeleteVehicleClassDialog: PropTypes.func,
  setPage: PropTypes.func,
  page: PropTypes.number,
  rowsPerPage: PropTypes.number,
  refreshVehicleClass: PropTypes.func,
};
